import { useArchivedProposals } from "./hooks/useArchivedProposals";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { Button, Page, PageContentHeader, PageEmptyState, Searchbox, TableList, SortDirection, SortFieldType, useFilterAndSortData } from 'seagull';
import { useMemo, useState } from 'react';
import { CreateProposalButton } from "../../components/CreateProposalButton/CreateProposalButton";
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { ArchivedProposalRow } from "./ArchivedProposalRow/ArchivedProposalRow";
import styles from "./ArchivedProposalsDashboard.module.scss";
import ArchivedProposalsEmptyState from "./assets/ArchivedProposalsEmptyState.svg";
import PageLoadingState from "../../components/PageLoadingState";
import { getUICompanyName } from "./ArchivedProposalsDashboard.utils";
import { pathgen } from 'object-path-generator';
import { useStore } from "../../contexts";
import { selectors } from "../../store/state-selectors";
import { useContactsMap } from "../../hooks/reactQuery/derived/useContactsMap";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const ArchivedProposalsDashboardTestIds = pathgen('ArchivedProposalsDashboardTestIds');
export let ColumnIds = /*#__PURE__*/function (ColumnIds) {
  ColumnIds[ColumnIds["CompanyName"] = 0] = "CompanyName";
  ColumnIds[ColumnIds["ArchivedBy"] = 1] = "ArchivedBy";
  ColumnIds[ColumnIds["ArchivedDate"] = 2] = "ArchivedDate";
  return ColumnIds;
}({});
const columnDefs = {
  [ColumnIds.CompanyName]: {
    label: 'Company name',
    type: SortFieldType.String,
    getter: 'value',
    getValue: draft => "".concat(getUICompanyName(draft), " ").concat(draft.title)
  },
  [ColumnIds.ArchivedBy]: {
    label: 'Archived by',
    type: SortFieldType.String,
    getter: 'value',
    getValue: draft => "".concat(draft.lastModifiedBy.firstName, " ").concat(draft.lastModifiedBy.lastName)
  },
  [ColumnIds.ArchivedDate]: {
    label: 'Archived Date',
    type: SortFieldType.Date,
    getter: 'path',
    path: 'lastModifiedOn'
  }
};
const TableColumnDefs = [{
  width: [180, 500],
  sortable: true,
  value: columnDefs[ColumnIds.CompanyName].label
}, {
  width: [120, 390],
  sortable: true,
  value: columnDefs[ColumnIds.ArchivedBy].label
}, {
  width: [120, 390],
  sortable: true,
  value: columnDefs[ColumnIds.ArchivedDate].label
}];
export const ArchivedProposalsDashboard = () => {
  var _drafts$length;
  const {
    drafts: rawDrafts,
    isLoading,
    isBackgroundLoading,
    refresh
  } = useArchivedProposals({
    refreshOnMount: true
  });
  const worksAt = useStore(selectors.user.business);
  const {
    contactsMap,
    sampleContact
  } = useContactsMap(worksAt === null || worksAt === void 0 ? void 0 : worksAt.id);
  const [tableSort, setTableSort] = useState({
    colIndex: ColumnIds.ArchivedDate,
    sort: SortDirection.Desc
  });
  const {
    urlState: {
      search
    },
    changeUrlState
  } = useStateFromUrl({
    search: {
      defaultValue: ''
    }
  });
  const filter = useMemo(() => ({
    [ColumnIds.CompanyName]: {
      search
    }
  }), [search]);
  const sort = useMemo(() => tableSort ? {
    [tableSort.colIndex]: {
      direction: tableSort.sort
    }
  } : undefined, [tableSort]);
  const {
    data: drafts
  } = useFilterAndSortData({
    data: rawDrafts,
    columnDefs,
    filter,
    sort
  });
  useSetDocumentTitle('Archived proposals');
  if (isLoading) {
    return /*#__PURE__*/_jsx(PageLoadingState, {});
  }
  const hasDrafts = rawDrafts && rawDrafts.length > 0;
  const onSortChange = sort => {
    setTableSort(sort);
  };
  const onClearSearch = () => {
    changeUrlState({
      search: ''
    });
  };
  return /*#__PURE__*/_jsx(TableList.Provider, {
    hasActionColumn: true,
    columnDefs: TableColumnDefs,
    sort: tableSort,
    onSortChange: onSortChange,
    numRows: (_drafts$length = drafts === null || drafts === void 0 ? void 0 : drafts.length) !== null && _drafts$length !== void 0 ? _drafts$length : 0,
    children: /*#__PURE__*/_jsx(Page, {
      "data-testid": ArchivedProposalsDashboardTestIds.Root(),
      header: "Archived proposals",
      actions: /*#__PURE__*/_jsx(CreateProposalButton, {}),
      loading: isBackgroundLoading,
      children: hasDrafts ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(PageContentHeader, {
          children: [/*#__PURE__*/_jsx(Searchbox, {
            className: styles.searchbox,
            registerHotkey: true,
            "data-testid": ArchivedProposalsDashboardTestIds.Search(),
            value: search,
            onChange: event => {
              const searchStr = event.target.value;
              changeUrlState({
                search: searchStr
              });
            },
            onClear: onClearSearch
          }), /*#__PURE__*/_jsx(TableList.Header, {
            items: TableColumnDefs,
            "data-testid": ArchivedProposalsDashboardTestIds.TableHeader()
          })]
        }), drafts !== null && drafts !== void 0 && drafts.length ? drafts.map((draft, i) => /*#__PURE__*/_jsx(ArchivedProposalRow, {
          draft: draft,
          index: i,
          refresh: refresh,
          contactsMap: contactsMap,
          sampleContact: sampleContact
        }, draft.id)) : /*#__PURE__*/_jsx(PageEmptyState, {
          illustration: /*#__PURE__*/_jsx(ArchivedProposalsEmptyState, {}),
          title: "No archived proposals match your request",
          message: "Try adjusting your search",
          "data-testid": ArchivedProposalsDashboardTestIds.EmptySearch.Root(),
          actions: /*#__PURE__*/_jsx(Button, {
            "data-testid": ArchivedProposalsDashboardTestIds.EmptySearch.ClearSearch(),
            onClick: onClearSearch,
            children: "Clear search"
          })
        })]
      }) : /*#__PURE__*/_jsx(PageEmptyState, {
        "data-testid": ArchivedProposalsDashboardTestIds.EmptyState.Root(),
        illustration: /*#__PURE__*/_jsx(ArchivedProposalsEmptyState, {}),
        title: "No archived proposals yet",
        message: "When you archive a proposal, it stays here for reference.",
        actions: /*#__PURE__*/_jsx(_Fragment, {
          children: /*#__PURE__*/_jsx(CreateProposalButton, {
            "data-testid": ArchivedProposalsDashboardTestIds.EmptyState.CreateProposalButton(),
            text: "Create a proposal",
            hidePrefix: true
          })
        })
      })
    })
  });
};